import React from "react"

import { Badge, Tooltip } from "@coval/ui"

interface TestSetVersionTagProps {
  /** Test-set version number the run executed against (`Run.test_set_version`). */
  ranVersion?: number | null
  /** The test set's current (live) version number. */
  currentVersion?: number | null
  /**
   * "full" renders a labelled pill ("Ran with v2 — current is v4"); "compact" renders a small
   * version chip ("v2") for dense surfaces. Both share one tooltip.
   */
  variant?: "full" | "compact"
  className?: string
}

/**
 * A run reflects the test-set version it executed against, which can fall behind the live test set
 * under the copy-on-save model. The tag flags that gap so an old run is never misread as the current
 * test set.
 *
 * Stale only when both versions are known and differ — runs whose test set predates versioning carry
 * no version number and render nothing.
 */
export function isTestSetVersionStale(
  ranVersion?: number | null,
  currentVersion?: number | null,
): boolean {
  return (
    ranVersion != null &&
    currentVersion != null &&
    ranVersion !== currentVersion
  )
}

/**
 * Warning indicator shown when a run executed against a stale test-set version. Renders nothing when
 * the run is on the test set's current version (see isTestSetVersionStale).
 */
export function TestSetVersionTag({
  ranVersion,
  currentVersion,
  variant = "full",
  className,
}: TestSetVersionTagProps): React.ReactElement | null {
  if (!isTestSetVersionStale(ranVersion, currentVersion)) {
    return null
  }

  const tooltip = `This run used test set v${ranVersion}. The test set is now on v${currentVersion}.`

  if (variant === "compact") {
    return (
      <Tooltip content={tooltip}>
        <Badge variant="warning" className={`shrink-0 ${className ?? ""}`}>
          v{ranVersion}
        </Badge>
      </Tooltip>
    )
  }

  return (
    <Tooltip content={tooltip}>
      <Badge variant="warning" className={className}>
        Ran with v{ranVersion} — current is v{currentVersion}
      </Badge>
    </Tooltip>
  )
}
